import React, {useEffect, useState} from "react";
import "./topbar.css";
import {
    Logout,
    PersonOutline,
    Search as SearchIcon,
    Dashboard as DashboardIcon,
    Home as HomeIcon,
    Settings as SettingsIcon,
    AccountCircle as AccountCircleIcon,
    KeyboardArrowRight as ArrowRightIcon
} from "@mui/icons-material";
import {
    Avatar,
    Menu,
    MenuItem,
    useMediaQuery,
    Tooltip,
    IconButton,
    Box,
    Divider,
    ListItemIcon,
    Fade,
    InputBase,
    Paper,
    Typography,
    useTheme,
    Switch,
    Skeleton,
    Button,
    ListItemText
} from "@mui/material";
import {Link, useLocation, useNavigate} from "react-router-dom";
import CryptoJS from "crypto-js";
import {KEY} from "../../dummyData";

export default function Topbar(props) {
    // Get current location and navigation
    const location = useLocation();
    const navigate = useNavigate();

    // Check if current path is root
    const isRootPath = location.pathname === "/";

    // State variables
    const [anchorEl, setAnchorEl] = useState(null);
    const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
    const [notificationsNumber, setNotificationsNumber] = useState(6);
    const [initialName, setInitialName] = useState("New User");
    const [name, setName] = useState(initialName);
    const [showNotifications, setShowNotifications] = useState(false);
    const [loading, setLoading] = useState(true);
    const [propertyName, setPropertyName] = useState("");
    const [unitNumber, setUnitNumber] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [showSearch, setShowSearch] = useState(false);

    // Media queries for responsive design
    const isMobile = useMediaQuery("(max-width:700px)");
    const isSmallScreen = useMediaQuery("(max-width:900px)");
    const theme = useTheme();

    // Menu states
    const menuOpen = Boolean(anchorEl);
    const notificationsOpen = Boolean(notificationsAnchorEl);

    // Get user data from localStorage
    useEffect(() => {
        setLoading(true);
        try {
            let originalText;
            if (localStorage.getItem("tenant_details") === null) {
                let dummyData = {
                    first_name: "",
                    last_name: "",
                };
                originalText = JSON.stringify(dummyData);
            } else {
                let details = CryptoJS.AES.decrypt(
                    localStorage.getItem("tenant_details"),
                    KEY
                );
                originalText = details.toString(CryptoJS.enc.Utf8);
            }

            let tenantDetails = new Map(Object.entries(JSON.parse(originalText)));
            const firstName = tenantDetails.get("first_name") || "";
            const lastName = tenantDetails.get("last_name") || "";
            const property = tenantDetails.get("property_name") || "";
            const unit = tenantDetails.get("house_number") || "";

            setInitialName(firstName + " " + lastName);
            setPropertyName(property);
            setUnitNumber(unit);

            if (isMobile) {
                setName(firstName);
            } else {
                setName(firstName + " " + lastName);
            }

            // Simulate loading
            setTimeout(() => {
                setLoading(false);
            }, 800);
        } catch (e) {
            console.error("Error parsing user data:", e);
            setLoading(false);
        }
    }, [isMobile, props.isLoggedIn]);

    // Handle profile menu
    const handleProfileClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    // Handle notifications menu
    const handleNotificationsClick = (event) => {
        setNotificationsAnchorEl(event.currentTarget);
        setShowNotifications(true);
    };

    // Close menus
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleNotificationsClose = () => {
        setNotificationsAnchorEl(null);
        setShowNotifications(false);
    };

    // Handle navigation
    const handleNavigation = (path) => {
        navigate(path);
        handleMenuClose();
    };

    // Handle logout
    const logout = () => {
        localStorage.clear();
        handleMenuClose();
        props.onLogout && props.onLogout();
    };

    // Handle search
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        // Implement search functionality
        console.log("Searching for:", searchQuery);
        setSearchQuery("");
        setShowSearch(false);
    };

    const toggleSearch = () => {
        setShowSearch(!showSearch);
        if (!showSearch) {
            setTimeout(() => {
                document.getElementById("search-input")?.focus();
            }, 100);
        }
    };

    // Mark all notifications as read
    const markAllAsRead = () => {
        setNotificationsNumber(0);
        handleNotificationsClose();
    };

    // Get initials for avatar
    const getInitials = (string) => {
        if (!string || string.trim() === "") return "NU";

        let names = string.split(" "),
            initials = names[0].substring(0, 1).toUpperCase();

        if (names.length > 1) {
            initials += names[names.length - 1].substring(0, 1).toUpperCase();
        }
        return initials;
    };

    // Don't render component if we're on root path
    if (isRootPath) {
        return null;
    }

    return (
        <Paper
            elevation={1}
            sx={{
                borderRadius: 0,
                position: 'sticky',
                top: 0,
                zIndex: 1100,
                backgroundColor: 'white',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                color: 'inherit',
                transition: 'all 0.3s ease'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: {xs: '8px 16px', md: '8px 24px'},
                    height: {xs: '64px', md: '70px'}
                }}
            >
                {/* Left section */}
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            ml: {xs: 0, sm: 1}
                        }}
                    >
                        {loading ? (
                            <>
                                <Skeleton variant="text" width={120} height={24}/>
                                <Skeleton variant="text" width={160} height={16}/>
                            </>
                        ) : (
                            <>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: {xs: '1rem', md: '1.1rem'},
                                        color: '#334155',
                                        lineHeight: 1.2
                                    }}
                                >
                                    Welcome, {name}
                                </Typography>
                                {(propertyName || unitNumber) && !isSmallScreen && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'rgba(0,0,0,0.6)',
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        {propertyName}{unitNumber ? ` • Unit ${unitNumber}` : ''}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                </Box>

                {/* Right section */}
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    {/* Profile Avatar */}
                    <Box sx={{ml: 1}}>
                        <Tooltip title="Account menu">
                            <IconButton
                                onClick={handleProfileClick}
                                size="small"
                                sx={{
                                    p: 0.5,
                                    border: '2px solid rgba(0,0,0,0.05)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: '#3B82F6',
                                        transform: 'scale(1.05)'
                                    }
                                }}
                            >
                                {loading ? (
                                    <Skeleton variant="circular" width={40} height={40}/>
                                ) : (
                                    <Avatar
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            bgcolor: '#3B82F6',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        {getInitials(name)}
                                    </Avatar>
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Enhanced Profile Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    TransitionComponent={Fade}
                    transformOrigin={{horizontal: "right", vertical: "top"}}
                    anchorOrigin={{horizontal: "right", vertical: "bottom"}}
                    PaperProps={{
                        sx: {
                            mt: 1.5,
                            width: 280,
                            borderRadius: '16px',
                            overflow: 'visible',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            border: '1px solid rgba(0,0,0,0.08)',
                            bgcolor: 'white',
                            '&:before': {
                                content: '""',
                                position: 'absolute',
                                top: -7,
                                right: 20,
                                width: 14,
                                height: 14,
                                bgcolor: 'white',
                                transform: 'rotate(45deg)',
                                zIndex: 0,
                                borderTop: '1px solid rgba(0,0,0,0.08)',
                                borderLeft: '1px solid rgba(0,0,0,0.08)'
                            }
                        }
                    }}
                >
                    {/* Profile Header */}
                    <Box
                        sx={{
                            p: 3,
                            textAlign: 'center',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '1px',
                                background: 'rgba(255,255,255,0.2)'
                            }
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 64,
                                height: 64,
                                margin: '0 auto 12px',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                border: '3px solid rgba(255,255,255,0.3)'
                            }}
                        >
                            {getInitials(initialName)}
                        </Avatar>
                        <Typography variant="h6" sx={{fontWeight: 600, fontSize: '1.1rem', mb: 0.5}}>
                            {initialName}
                        </Typography>
                        {(propertyName || unitNumber) && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'rgba(255,255,255,0.8)',
                                    fontSize: '0.85rem'
                                }}
                            >
                                {propertyName}{unitNumber ? ` • Unit ${unitNumber}` : ''}
                            </Typography>
                        )}
                    </Box>

                    {/* Menu Items */}
                    <Box sx={{py: 1}}>
                        <MenuItem
                            onClick={() => handleNavigation('/profile')}
                            sx={{
                                py: 1.5,
                                px: 3,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: '#F8FAFC',
                                    transform: 'translateX(4px)'
                                }
                            }}
                        >
                            <ListItemIcon>
                                <PersonOutline
                                    fontSize="small"
                                    sx={{color: '#3B82F6'}}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary="My Profile"
                                primaryTypographyProps={{
                                    fontSize: '0.95rem',
                                    fontWeight: 500
                                }}
                            />
                            <ArrowRightIcon
                                sx={{
                                    color: '#9CA3AF',
                                    fontSize: '1rem',
                                    opacity: 0,
                                    transition: 'opacity 0.2s ease'
                                }}
                                className="arrow-icon"
                            />
                        </MenuItem>

                        <MenuItem
                            onClick={() => handleNavigation('/home')}
                            sx={{
                                py: 1.5,
                                px: 3,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: '#F8FAFC',
                                    transform: 'translateX(4px)',
                                    '& .arrow-icon': {
                                        opacity: 1
                                    }
                                }
                            }}
                        >
                            <ListItemIcon>
                                <DashboardIcon
                                    fontSize="small"
                                    sx={{color: '#10B981'}}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary="Dashboard"
                                primaryTypographyProps={{
                                    fontSize: '0.95rem',
                                    fontWeight: 500
                                }}
                            />
                            <ArrowRightIcon
                                sx={{
                                    color: '#9CA3AF',
                                    fontSize: '1rem',
                                    opacity: 0,
                                    transition: 'opacity 0.2s ease'
                                }}
                                className="arrow-icon"
                            />
                        </MenuItem>

                        <MenuItem
                            sx={{
                                py: 1.5,
                                px: 3,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: '#F8FAFC',
                                    transform: 'translateX(4px)',
                                    '& .arrow-icon': {
                                        opacity: 1
                                    }
                                }
                            }}
                        >
                            <ListItemIcon>
                                <SettingsIcon
                                    fontSize="small"
                                    sx={{color: '#8B5CF6'}}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary="Settings"
                                primaryTypographyProps={{
                                    fontSize: '0.95rem',
                                    fontWeight: 500
                                }}
                            />
                            <ArrowRightIcon
                                sx={{
                                    color: '#9CA3AF',
                                    fontSize: '1rem',
                                    opacity: 0,
                                    transition: 'opacity 0.2s ease'
                                }}
                                className="arrow-icon"
                            />
                        </MenuItem>
                    </Box>

                    <Divider sx={{my: 1, mx: 2}}/>

                    {/* Logout */}
                    <Box sx={{p: 1}}>
                        <MenuItem
                            onClick={logout}
                            sx={{
                                py: 1.5,
                                px: 3,
                                color: '#EF4444',
                                transition: 'all 0.2s ease',
                                borderRadius: '8px',
                                mx: 1,
                                '&:hover': {
                                    backgroundColor: '#FEF2F2',
                                    transform: 'translateX(4px)',
                                    '& .arrow-icon': {
                                        opacity: 1
                                    }
                                }
                            }}
                        >
                            <ListItemIcon>
                                <Logout
                                    fontSize="small"
                                    sx={{color: '#EF4444'}}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary="Sign out"
                                primaryTypographyProps={{
                                    fontSize: '0.95rem',
                                    fontWeight: 500,
                                    color: '#EF4444'
                                }}
                            />
                            <ArrowRightIcon
                                sx={{
                                    color: '#EF4444',
                                    fontSize: '1rem',
                                    opacity: 0,
                                    transition: 'opacity 0.2s ease'
                                }}
                                className="arrow-icon"
                            />
                        </MenuItem>
                    </Box>
                </Menu>

                {/* Notifications Menu - Keeping original for reference */}
                <Menu
                    anchorEl={notificationsAnchorEl}
                    open={notificationsOpen}
                    onClose={handleNotificationsClose}
                    TransitionComponent={Fade}
                    transformOrigin={{horizontal: "right", vertical: "top"}}
                    anchorOrigin={{horizontal: "right", vertical: "bottom"}}
                    PaperProps={{
                        sx: {
                            mt: 1.5,
                            maxWidth: 360,
                            width: {xs: 'calc(100vw - 32px)', sm: 360},
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            border: '1px solid rgba(0,0,0,0.08)',
                            bgcolor: 'white',
                            '&:before': {
                                content: '""',
                                position: 'absolute',
                                top: -6,
                                right: 14,
                                width: 12,
                                height: 12,
                                bgcolor: 'white',
                                transform: 'rotate(45deg)',
                                zIndex: 0,
                                borderTop: '1px solid rgba(0,0,0,0.08)',
                                borderLeft: '1px solid rgba(0,0,0,0.08)'
                            }
                        }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            borderBottom: '1px solid rgba(0,0,0,0.08)'
                        }}
                    >
                        <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                            Notifications
                        </Typography>
                        <Button
                            size="small"
                            sx={{
                                color: '#3B82F6',
                                textTransform: 'none',
                                fontSize: '0.8rem'
                            }}
                            onClick={markAllAsRead}
                        >
                            Mark all as read
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            p: 1.5,
                            borderTop: '1px solid rgba(0,0,0,0.08)',
                            textAlign: 'center'
                        }}
                    >
                        <Button
                            fullWidth
                            sx={{
                                textTransform: 'none',
                                color: '#3B82F6'
                            }}
                        >
                            View all notifications
                        </Button>
                    </Box>
                </Menu>
            </Box>

            {/* Add custom styles for hover effects */}
            <style jsx>{`
                .MuiMenuItem-root:hover .arrow-icon {
                    opacity: 1 !important;
                }
            `}</style>
        </Paper>
    );
}